import { createMemo, createResource, For, Show } from "solid-js";
import { useNavigate } from "@solidjs/router";
import type { LatestValue, MeasurementPoint } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo, formatNumber } from "../format.ts";
import { setScope } from "../context.ts";
import { Icon } from "../icons.tsx";
import { HIGH_V, LOW_V } from "./MeterParameters.tsx";

const PHASES = ["phase1", "phase2", "phase3"];

interface Meter {
  deviceId: string;
  name: string;
  pointId: string;
}

/** The electricity meters among the points: one per device, named after its points (without the channel part). */
export function metersOf(points: readonly MeasurementPoint[]): Meter[] {
  const byDevice = new Map<string, MeasurementPoint[]>();
  for (const p of points) {
    if (
      p.binding &&
      (p.medium === "el" || p.medium === "pv" || p.medium === "bess")
    ) {
      const list = byDevice.get(p.binding.deviceId) ?? [];
      list.push(p);
      byDevice.set(p.binding.deviceId, list);
    }
  }
  return [...byDevice.entries()].map(([deviceId, list]) => {
    const main =
      list.find((p) => p.binding!.channel === "main") ??
      list.find((p) => p.role === "main") ??
      list[0]!;
    const name =
      list.length > 1 ? main.name.split(" · ")[0]!.trim() : main.name;
    return { deviceId, name, pointId: main.id };
  });
}

/**
 * Meters now: per meter its power, the voltage and current of each phase, the power factor and the frequency — the
 * quantities a meter reports besides energy — with a way to their history (Analyses → meter parameters).
 */
export function MetersNow(props: {
  points: readonly MeasurementPoint[];
  placeId: string;
}) {
  const meters = createMemo(() => metersOf(props.points));
  const navigate = useNavigate();
  const open = (m: Meter) => {
    setScope(props.placeId);
    navigate(`/analysis?type=par&point=${encodeURIComponent(m.pointId)}`);
  };
  return (
    <Show when={meters().length > 0}>
      <section class="card">
        <div class="card-h">
          <h2>{t("places.meters.title")}</h2>
          <span class="meta">{t("places.meters.meta")}</span>
        </div>
        <div class="scrollx">
          <table class="tbl meters-now">
            <thead>
              <tr>
                <th>{t("places.meters.meter")}</th>
                <th class="num">{t("places.meters.power")}</th>
                <th class="num">{t("places.meters.voltage")}</th>
                <th class="num">{t("places.meters.current")}</th>
                <th class="num">cos φ</th>
                <th class="num">{t("places.meters.frequency")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={meters()}>
                {(m) => <MeterRow meter={m} onOpen={() => open(m)} />}
              </For>
            </tbody>
          </table>
        </div>
      </section>
    </Show>
  );
}

function MeterRow(props: { meter: Meter; onOpen: () => void }) {
  const [latest] = createResource(
    () => props.meter.deviceId,
    (id) => api.devices.latest(id),
  );
  const value = (attribute: string, channel?: string) =>
    (latest() ?? []).find(
      (v) =>
        v.attribute === attribute &&
        v.number !== null &&
        (channel === undefined || v.channel === channel),
    );
  const phases = (attribute: string) =>
    PHASES.map((c) => value(attribute, c)).filter(
      (v): v is LatestValue => v !== undefined,
    );
  const power = () => {
    const v = value("power.active", "main") ?? value("power.active");
    if (!v) {
      return "—";
    }
    const k = v.unit === "W" ? v.number! / 1000 : v.number!;
    return `${formatNumber(k, locale(), Math.abs(k) < 10 ? 2 : 1)} kW`;
  };
  const list = (attribute: string, digits: number, unit: string) => {
    const ph = phases(attribute);
    const all = ph.length > 0 ? ph : [value(attribute)].filter((v) => v);
    return all.length === 0
      ? "—"
      : `${all.map((v) => formatNumber(v!.number, locale(), digits)).join(" / ")} ${unit}`;
  };
  const voltageOff = () =>
    phases("voltage").some((v) => v.number! < LOW_V || v.number! > HIGH_V);
  const newest = () =>
    (latest() ?? []).reduce<string | null>(
      (a, v) => (a === null || v.at > a ? v.at : a),
      null,
    );
  return (
    <tr>
      <td>
        <b style={{ "font-weight": 500 }}>{props.meter.name}</b>
        <div class="hint">{newest() ? formatAgo(newest()!, locale()) : ""}</div>
      </td>
      <td class="num">{power()}</td>
      <td class="num">
        <span class={voltageOff() ? "crit-t" : ""}>
          {list("voltage", 1, "V")}
        </span>
      </td>
      <td class="num">{list("current", 2, "A")}</td>
      <td class="num">{list("power.factor", 2, "")}</td>
      <td class="num">
        {value("frequency")
          ? `${formatNumber(value("frequency")!.number, locale(), 2)} Hz`
          : "—"}
      </td>
      <td class="r">
        <button type="button" class="btn sm" onClick={() => props.onOpen()}>
          <Icon name="chart" />
          {t("places.meters.history")}
        </button>
      </td>
    </tr>
  );
}
